import { useState, useEffect } from 'react'
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Button, Select } from 'antd'
import {
  ApiOutlined,
  UserOutlined,
  RobotOutlined,
  DollarOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  SafetyOutlined,
  GiftOutlined,
  AppstoreOutlined,
  HeartOutlined,
  VideoCameraOutlined,
  SendOutlined,
  MessageOutlined,
  AreaChartOutlined,
  CrownOutlined,
  SlidersOutlined,
  PictureOutlined,
  ExperimentOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router'
import { canAccessRoute, isSuperAdmin, resolvePageDefinition } from '@/lib/adminPermission'
import { useAuthStore } from '@/store/authStore'
import { usePackageStore } from '@/store/packageStore'
import { apiClient } from '@/lib/api'
import { useEnv } from '@/providers/EnvProvider'
import { confirmUnsavedChangesNavigation } from '@/lib/unsavedChangesGuard'
import { isProductionRuntime } from '@/lib/runtime-env'
import type { MenuProps } from 'antd'

const { Header, Sider, Content } = Layout

type MenuItem = Required<MenuProps>['items'][number]

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const env = useEnv()
  const showApiRequestStats = !isProductionRuntime(env)
  const user = useAuthStore((state) => state.user)
  const loading = useAuthStore((state) => state.loading)
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const logout = useAuthStore((state) => state.logout)

  // 检查登录状态
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // 未登录则跳转到登录页
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login')
    }
  }, [user, loading, navigate])

  // 所有父菜单的 keys
  const rootSubmenuKeys = [
    'analytics',
    'users',
    'characters',
    'content',
    'payment',
    'finance',
    'operations',
    'push',
    'campaigns',
    'chat-ai',
    'agent-harness',
    'observability',
    'statistics',
    'moderation',
    'settings',
    'chat-retrieval',
    'visual-replacement',
    'match',
    'anchors',
    'videocall',
    'rooms',
  ]

  const selectedApp = usePackageStore((state) => state.selectedApp)
  const appList = usePackageStore((state) => state.appList)
  const setSelectedApp = usePackageStore((state) => state.setSelectedApp)
  const setAppList = usePackageStore((state) => state.setAppList)

  // 获取应用列表
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const response = await apiClient.get<{
          apps: Array<{ id: string; package_name: string; name?: string }>
          total: number
        }>('/admin-api/v1/admin/apps/all')
        if (response.code === 0 && response.data.apps) {
          const apps = response.data.apps
          setAppList(apps)
        }
      } catch (error) {
        console.error('Failed to fetch apps:', error)
      }
    }
    fetchApps()
  }, [])

  const handleLogout = async () => {
    if (!confirmUnsavedChangesNavigation()) return
    await logout()
    navigate('/login')
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '账号设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout,
    },
  ]

  const menuItems: MenuItem[] = [
    {
      key: 'analytics',
      icon: <AreaChartOutlined />,
      label: '数据大盘',
      children: [
        { key: '/analytics/reports/create', label: '创建报表' },
        { key: '/analytics/reports/view', label: '查看报表' },
      ],
    },
    {
      key: 'campaigns',
      icon: <SendOutlined />,
      label: '投放管理',
      children: [
        { key: '/campaigns/overview', label: '投放概览' },
        { key: '/campaigns/records', label: '访问与归因' },
        { key: '/campaigns/sites', label: '推广站点' },
        { key: '/campaigns/rules', label: '规则与判定' },
        { key: '/campaigns/integrations', label: '接入状态' },
      ],
    },
    {
      key: '/service-monitor',
      icon: <ApiOutlined />,
      label: '服务监测',
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: '用户管理',
      children: [
        { key: '/users', label: '用户列表' },
        { key: '/users/first-24h-swipe-stats', label: '24H划卡统计' },
        { key: '/users/device-attribution-events', label: '归因事件' },
        { key: '/users/admins', label: '管理员' },
      ],
    },
    {
      key: 'characters',
      icon: <RobotOutlined />,
      label: '角色管理',
      children: [
        { key: '/characters', label: '角色列表' },
        { key: '/characters/audit', label: '角色审核' },
        { key: '/characters/categories', label: '角色分类' },
        { key: '/characters/tags', label: '角色标签' },
        { key: '/characters/looking-for-tags', label: '约会目的标签' },
        { key: '/characters/type-prefer-tags', label: '意向类型标签' },
        { key: '/characters/template-variables', label: '角色模板变量' },
        { key: '/characters/system-template-variables', label: '系统模板变量' },
        { key: '/characters/stats', label: '角色统计' },
      ],
    },
    {
      key: 'payment',
      icon: <DollarOutlined />,
      label: '支付管理',
      children: [
        { key: '/payment/orders', label: '支付订单' },
        { key: '/payment/recharge-users', label: '充值用户分析' },
        { key: '/payment/refunds', label: '退款管理' },
        { key: '/payment/fulfillments', label: '发货监控' },
        { key: '/payment/subscriptions', label: '订阅管理' },
        { key: '/payment/products', label: '商品配置' },
        { key: '/payment/webhook-logs', label: 'Webhook日志' },
        { key: '/payment/google-pay-configs', label: 'Google Pay配置' },
        { key: '/payment/apple-pay-configs', label: 'Apple Pay配置' },
        { key: '/payment/third-party-configs', label: '第三方支付配置' },
        { key: '/payment/route-configs', label: '支付列表设置' },
        { key: '/payment/traces', label: '三方支付看板' },
      ],
    },
    {
      key: 'operations',
      icon: <GiftOutlined />,
      label: '运营管理',
      children: [
        { key: '/operations/activities', label: '活动管理' },
        { key: '/operations/rewards', label: '奖励管理' },
        { key: '/operations/checkin', label: '签到配置' },
        { key: '/operations/invitation', label: '邀请活动' },
        { key: '/operations/tasks', label: '任务系统' },
        { key: '/operations/vip', label: 'VIP配置' },
        { key: '/operations/character-generation', label: '角色生成配置' },
        { key: '/operations/trending-keywords', label: '热词管理' },
        { key: '/operations/announcements', label: '系统公告' },
        { key: '/operations/notifications', label: '用户通知' },
      ],
    },
    {
      key: 'push',
      icon: <SendOutlined />,
      label: '推送管理',
      children: [
        { key: '/push/stats', label: '推送统计' },
        { key: '/push/templates', label: '推送模板' },
        { key: '/push/tasks', label: '推送任务' },
        { key: '/push/test', label: '推送测试' },
        { key: '/push/config', label: '推送配置' },
        { key: '/push/recall-logs', label: '召回记录' },
      ],
    },
    {
      key: 'chat-ai',
      icon: <ExperimentOutlined />,
      label: 'Chat AI',
      children: [
        { key: '/chat-ai/prompt-workspace', label: 'Prompt 工作台' },
        { key: '/chat-ai/agent-flows', label: 'Agent Flow' },
        { key: '/chat-ai/flow-routing', label: 'Flow 流量控制' },
        { key: '/chat-ai/agent-flow-node-catalogs', label: '节点目录' },
        { key: '/chat-ai/prompt-sample-sets', label: 'Prompt样本集' },
        { key: '/chat-ai/prompt-experiments', label: 'Prompt实验' },
        { key: '/chat-ai/prompt-review-center', label: 'Prompt评审中心' },
        { key: '/chat-ai/prompt-evaluation-center', label: '测评中心' },
      ],
    },
    {
      key: 'agent-harness',
      icon: <RobotOutlined />,
      label: 'Agent Harness',
      children: [
        { key: '/agent-harness/runs', label: '执行记录' },
        { key: '/agent-harness/followup-schedules', label: '追发调度' },
        { key: '/agent-harness/config', label: '配置' },
      ],
    },
    {
      key: 'observability',
      icon: <AreaChartOutlined />,
      label: '监控分析',
      children: [
        { key: '/observability/user-behavior-monitor', label: '行为时间线监控' },
        ...(showApiRequestStats
          ? [{ key: '/observability/api-request-stats', label: '接口请求统计' }]
          : []),
      ],
    },
    {
      key: 'moderation',
      icon: <SafetyOutlined />,
      label: '审核管理',
      children: [
        { key: '/moderation/access-control', label: 'IP访问控制' },
        { key: '/reports', label: '举报处理' },
      ],
    },
    {
      key: 'rooms',
      icon: <MessageOutlined />,
      label: '聊天管理',
      children: [
        { key: '/rooms', label: '房间列表' },
        { key: '/rooms/messages', label: '聊天记录' },
        { key: '/rooms/chat-replay', label: '聊天回放' },
        { key: '/rooms/chat-assets', label: '聊天素材' },
        { key: '/rooms/chat-corpus', label: '短聊语料库' },
        { key: '/rooms/chat-qa', label: '聊天问答库' },
        { key: '/rooms/unpaid-follow-up-stats', label: '追问效果统计' },
        { key: '/rooms/chat-relationship-stages', label: '聊天关系阶段配置' },
      ],
    },
    {
      key: 'chat-retrieval',
      icon: <SlidersOutlined />,
      label: '聊天检索管理',
      children: [
        { key: '/chat-retrieval/rag-config', label: 'RAG 配置' },
        { key: '/chat-retrieval/samples', label: '评测样本' },
        { key: '/chat-retrieval/evaluation', label: '检索评测' },
        { key: '/chat-retrieval/memory-tuning', label: '记忆调优' },
        { key: '/chat-retrieval/tuning', label: '自动调参' },
        { key: '/chat-retrieval/recall-logs', label: '召回日志' },
      ],
    },
    {
      key: 'image-gen-configs',
      icon: <PictureOutlined />,
      label: '图片生成管理',
      children: [
        { key: '/image-generation/tasks', label: '任务管理' },
        { key: '/image-generation/translation-records', label: '翻译记录' },
        { key: '/image-configs/types', label: '类型配置' },
        { key: '/image-configs/models', label: '模型配置' },
        { key: '/image-configs/styles', label: '风格配置' },
        { key: '/image-configs/tags', label: '标签配置' },
        { key: '/jimeng-whitelist', label: '既梦白名单' },
        { key: '/image-generation/lora-library', label: 'Lora库管理' },
        { key: '/comfyui-workflow-logs', label: 'ComfyUI工作流日志' },
        { key: '/jimeng-workflow-logs', label: '既梦工作流日志' },
      ],
    },
    {
      key: 'match',
      icon: <HeartOutlined />,
      label: '匹配管理',
      children: [
        { key: '/match/dashboard', label: '数据大盘' },
        { key: '/match/ai-female-users', label: 'AI女用户池' },
        { key: '/match/deleted-ai-female-users', label: '删除 AI 女用户' },
        { key: '/match/moments', label: '动态列表' },
        { key: '/match/user', label: '用户查询' },
        { key: '/match/results', label: '匹配记录' },
        { key: '/match/chatted-characters', label: '已聊记录' },
        { key: '/match/swipe-records', label: '划卡记录' },
        { key: '/match/quota', label: '配额管理' },
        { key: '/match/config', label: '匹配配置' },
        { key: '/match/stats', label: 'AI日志统计' },
        { key: '/match/diagnostics', label: '匹配诊断' },
        { key: '/match/city-neighbors', label: '周边城市缓存' },
        { key: '/match/admin-ops', label: '管理员操作' },
      ],
    },
    {
      key: 'anchors',
      icon: <CrownOutlined />,
      label: '主播管理',
      children: [
        { key: '/anchors/guilds', label: '工会配置' },
        { key: '/anchors/guild-invites', label: '我的公会邀请码' },
        { key: '/anchors/levels', label: '主播等级' },
        { key: '/anchors/level-rating-logs', label: '等级评级日志' },
        { key: '/anchors/pool', label: '主播池' },
        { key: '/anchors/call-records', label: '主播通话记录' },
        { key: '/anchors/frame-moderations', label: '视频抽帧审核' },
        { key: '/anchors/withdraw-requests', label: '主播提现记录' },
        { key: '/anchors/private-media', label: '私密媒体审核' },
        { key: '/anchors/revenue-dashboard', label: '工会收益看板' },
        { key: '/anchors/online-stats', label: '主播在线统计' },
      ],
    },
    {
      key: 'videocall',
      icon: <VideoCameraOutlined />,
      label: '视频通话',
      children: [{ key: '/videocall/dashboard', label: '通话监控' }],
    },
    {
      key: 'visual-replacement',
      icon: <PictureOutlined />,
      label: '视觉生成',
      children: [
        { key: '/visual-generation/tasks', label: '任务监控' },
        { key: '/visual-generation/preview', label: '项目预览' },
        { key: '/visual-replacement/app-configs', label: '应用配置' },
        { key: '/visual-replacement/pricing', label: '全局定价设置' },
        { key: '/visual-replacement/promotions', label: '促销管理' },
        {
          key: 'visual-replacement-image',
          label: '图片生成',
          children: [
            { key: '/visual-replacement/image/templates', label: '模板管理' },
            { key: '/visual-replacement/image/models', label: '模型管理' },
            { key: '/visual-replacement/image/prompts', label: '提示词管理' },
          ],
        },
        {
          key: 'visual-replacement-video',
          label: '视频生成',
          children: [
            { key: '/visual-replacement/video/templates', label: '模板管理' },
            { key: '/visual-replacement/video/streaming', label: '流媒体处理' },
            { key: '/visual-replacement/video/models', label: '模型管理' },
            { key: '/visual-replacement/video/prompts', label: '提示词管理' },
          ],
        },
        {
          key: 'visual-replacement-chat',
          label: '聊天管理',
          children: [
            { key: '/visual-replacement/chat/models', label: '模型管理' },
            { key: '/visual-replacement/chat/prompts', label: '提示词管理' },
          ],
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      children: [
        { key: '/settings/apps', label: 'APP管理' },
        { key: '/settings/app-store-links', label: '应用商店链接' },
        { key: '/settings/versions', label: 'APP版本管理' },
        { key: '/settings/base-limits', label: '基础限制配置' },
        { key: '/settings/service-accounts', label: '服务账号管理' },
        { key: '/settings/translation-configs', label: '翻译配置' },
        { key: '/settings/firebase', label: 'Firebase配置' },
        { key: '/settings/oauth-configs', label: '三方登录配置' },
        { key: '/settings/config', label: '配置管理 (未实现)', disabled: true },
        { key: '/settings/onelink-configs', label: 'OneLink配置' },
        { key: '/settings/chat-ai-configs', label: 'Chat AI配置' },
        { key: '/settings/ai-token-usage', label: '大模型管理' },
        { key: '/settings/alert-bots', label: '告警机器人' },
        { key: '/settings/app-costs', label: '应用费用配置' },
        { key: '/settings/system-configs', label: '系统配置' },
        { key: '/settings/interaction-signal-copy', label: '互动信号文案' },
        { key: '/settings/event-system', label: '事件系统' },
        { key: '/settings/redis-tools', label: 'Redis 管理' },
        { key: '/settings/first-login-rewards', label: '首次登录送金币' },
        { key: '/settings/app-version-review-rewards', label: '审核版本送金币' },

        { key: '/settings/operation-logs', label: '操作日志' },
      ],
    },
  ]

  const filterVisibleMenuItems = (items: MenuItem[]): MenuItem[] =>
    items
      .map((item) => {
        if (!item || !('key' in item)) {
          return item
        }

        if ('children' in item && item.children) {
          const visibleChildren = filterVisibleMenuItems(item.children as MenuItem[])
          if (visibleChildren.length === 0) {
            return null
          }
          return {
            ...item,
            children: visibleChildren,
          }
        }

        if (
          typeof item.key === 'string' &&
          item.key.startsWith('/') &&
          !canAccessRoute(user, item.key)
        ) {
          return null
        }

        return item
      })
      .filter(Boolean) as MenuItem[]

  const visibleMenuItems = filterVisibleMenuItems(menuItems)

  const currentPageAllowed = canAccessRoute(user, pathname)
  const currentPageDefinition = resolvePageDefinition(pathname)
  const canManagePermissions = isSuperAdmin(user)

  const findSelectedKey = (items: MenuItem[]): string => {
    let matchedKey = ''
    for (const item of items) {
      if (!item || !('key' in item)) continue
      if (typeof item.key === 'string' && item.key.startsWith('/')) {
        if (
          (pathname === item.key || pathname.startsWith(`${item.key}/`)) &&
          item.key.length >= matchedKey.length
        ) {
          matchedKey = item.key
        }
      }
      if ('children' in item && item.children) {
        const childMatch = findSelectedKey(item.children as MenuItem[])
        if (childMatch.length >= matchedKey.length) {
          matchedKey = childMatch
        }
      }
    }
    return matchedKey
  }

  const selectedKey = (() => {
    const matchedKey = findSelectedKey(visibleMenuItems)
    return matchedKey || pathname
  })()

  const handleMenuClick = (e: { key: string }) => {
    if (!confirmUnsavedChangesNavigation()) return
    navigate(e.key)
  }

  // 处理子菜单展开/折叠
  const handleOpenChange = (keys: string[]) => {
    // 找到最新打开的 key
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1)

    if (latestOpenKey && rootSubmenuKeys.indexOf(latestOpenKey) !== -1) {
      // 打开了一个根菜单 - 使用手风琴模式，只保留最新打开的
      setOpenKeys([latestOpenKey])
    } else {
      // 如果用户关闭了菜单，允许关闭；否则保持当前打开的菜单
      setOpenKeys(keys)
    }
  }

  // 根据当前路径自动展开对应的父菜单
  useEffect(() => {
    const findParentKeys = (items: MenuItem[], parents: string[] = []): string[] => {
      for (const item of items) {
        if (!item || !('key' in item)) continue
        const key = typeof item.key === 'string' ? item.key : ''
        if (key === selectedKey) return parents
        if ('children' in item && item.children) {
          const found = findParentKeys(
            item.children as MenuItem[],
            key ? [...parents, key] : parents
          )
          if (found.length > 0) return found
        }
      }
      return []
    }
    const parentKeys = findParentKeys(visibleMenuItems)
    if (parentKeys.length > 0) {
      setOpenKeys(parentKeys)
    }
  }, [selectedKey])

  // 如果正在加载或未登录，显示加载状态
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: collapsed ? 16 : 20,
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {collapsed ? 'EC' : 'EChat Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={visibleMenuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 40,
                height: 40,
              }}
            />
          </Space>

          <Space size="large">
            {/* 应用选择器 */}
            <Space>
              <AppstoreOutlined style={{ fontSize: 16, color: '#1890ff' }} />
              <Select
                value={selectedApp?.id}
                onChange={(appId) => {
                  if (!confirmUnsavedChangesNavigation()) return
                  const app = appList.find((a) => a.id === appId)
                  setSelectedApp(app || null)
                }}
                placeholder="选择应用"
                style={{ width: 250 }}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={appList.map((app) => ({
                  label: app.name ? `${app.name} (${app.package_name})` : app.package_name,
                  value: app.id,
                }))}
              />
            </Space>

            <Badge count={5}>
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username || '管理员'}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            margin: '24px',
            padding: 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          {currentPageAllowed ? (
            <Outlet />
          ) : (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-orange-200 bg-orange-50">
              <div className="max-w-xl text-center">
                <SafetyOutlined style={{ fontSize: 36, color: '#d97706', marginBottom: 12 }} />
                <h2 className="mb-2 text-lg font-semibold text-orange-900">当前页面未授权</h2>
                <p className="mb-2 text-sm text-orange-800">
                  {currentPageDefinition
                    ? `你当前账号未被授予“${currentPageDefinition.label}”页面权限。`
                    : '你当前账号未被授予访问当前页面的权限。'}
                </p>
                <p className="text-xs text-orange-700">
                  {canManagePermissions
                    ? '你是超级管理员，如仍看到此提示，请刷新页面后重试。'
                    : '请联系超级管理员在“用户管理 > 管理员 > 权限配置”里为你授权。'}
                </p>
              </div>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  )
}
